import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { PageLoader } from '@/components/feedback/PageLoader';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { AuthGuard } from '@/features/auth/AuthGuard';
import { EntitlementGuard } from '@/features/auth/EntitlementGuard';
import { FeatureRoute } from '@/features/auth/FeatureRoute';
import { navFeatureFor, navPaths } from '@/components/layout/nav-config';

const LoginRoute = lazy(() => import('@/features/auth/routes/LoginRoute'));
const HomeRoute = lazy(() => import('@/features/home/routes/HomeRoute'));
const DashboardRoute = lazy(() => import('@/features/dashboard/routes/DashboardRoute'));
const ConnectorsRoute = lazy(() => import('@/features/connectors/routes/ConnectorsRoute'));
const DecommissionDashboardRoute = lazy(
  () => import('@/features/decommission/routes/DecommissionDashboardRoute'),
);
const AllSystemsRoute = lazy(() => import('@/features/decommission/routes/AllSystemsRoute'));
const DevicesRoute = lazy(() => import('@/features/asset-management/routes/DevicesRoute'));
const IdentitiesRoute = lazy(() => import('@/features/asset-management/routes/IdentitiesRoute'));
const GraphRoute = lazy(() => import('@/features/orbis-intelligence/routes/GraphRoute'));
const InsightScanRoute = lazy(() => import('@/features/insight-scan/routes/InsightScanRoute'));
const ReportsRoute = lazy(() => import('@/features/reports/routes/ReportsRoute'));
const SprintsRoute = lazy(() => import('@/features/sprints/routes/SprintsRoute'));
const SectionPlaceholder = lazy(() => import('@/features/system/routes/SectionPlaceholder'));
const NotFoundRoute = lazy(() => import('@/features/system/routes/NotFoundRoute'));

/**
 * Single Suspense + ErrorBoundary boundary wraps every protected route.
 * Route chunk loading and data fetching (useSuspenseQuery) share this fallback.
 */
function ProtectedLayout() {
  return (
    <AuthGuard>
      <EntitlementGuard>
        <AppShell>
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </AppShell>
      </EntitlementGuard>
    </AuthGuard>
  );
}

// Nav destinations whose content is not yet designed render a placeholder so
// the constant shell stays fully navigable. Paths backed by a real page are
// excluded here.
const realPaths = new Set([
  '/home',
  '/connectors',
  '/reports',
  '/dashboard',
  '/sprints',
  '/decommission',
  '/asset-management/devices',
  '/asset-management/identities',
  '/orbis-intelligence/graph',
  '/orbis-intelligence/insight-scan',
]);
const placeholderPaths = navPaths.filter((p) => !realPaths.has(p));

const router = createBrowserRouter([
  {
    // Public — no AuthGuard, no app shell. OAuth-only sign-in screen.
    path: '/login',
    element: (
      <Suspense fallback={<PageLoader label="Loading…" />}>
        <LoginRoute />
      </Suspense>
    ),
  },
  {
    // Entra post-logout redirect URI (https://app.orbiswisdomx.com/logout).
    // Redirect to /login so the user sees the branded sign-in page.
    path: '/logout',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      { index: true, element: <Navigate to="/home" replace /> },
      { path: 'home', element: <HomeRoute /> },
      {
        path: 'dashboard',
        element: (
          <FeatureRoute feature="dashboard">
            <DashboardRoute />
          </FeatureRoute>
        ),
      },
      { path: 'connectors', element: <ConnectorsRoute /> },
      { path: 'decommission', element: <DecommissionDashboardRoute /> },
      { path: 'decommission/systems', element: <AllSystemsRoute /> },
      { path: 'asset-management', element: <Navigate to="/asset-management/devices" replace /> },
      {
        path: 'asset-management/devices',
        element: (
          <FeatureRoute feature="inventory">
            <DevicesRoute />
          </FeatureRoute>
        ),
      },
      {
        path: 'asset-management/identities',
        element: (
          <FeatureRoute feature="inventory">
            <IdentitiesRoute />
          </FeatureRoute>
        ),
      },
      { path: 'orbis-intelligence', element: <Navigate to="/orbis-intelligence/graph" replace /> },
      {
        path: 'orbis-intelligence/graph',
        element: (
          <FeatureRoute feature="scoring">
            <GraphRoute />
          </FeatureRoute>
        ),
      },
      {
        path: 'orbis-intelligence/insight-scan',
        element: (
          <FeatureRoute feature="scoring">
            <InsightScanRoute />
          </FeatureRoute>
        ),
      },
      { path: 'reports', element: <ReportsRoute /> },
      { path: 'sprints', element: <SprintsRoute /> },
      ...placeholderPaths.map((p) => {
        const feature = navFeatureFor(p);
        return {
          path: p.replace(/^\//, ''),
          element: feature ? (
            <FeatureRoute feature={feature}>
              <SectionPlaceholder />
            </FeatureRoute>
          ) : (
            <SectionPlaceholder />
          ),
        };
      }),
      { path: '*', element: <NotFoundRoute /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
